import { CSSProperties } from 'react';
import { useAppStore } from '../state/store';
import { EffectRow, FxAdd } from './mixer/EffectRow';
import { Knob } from './widgets/Knob';
import { Meter } from './widgets/Meter';
import { VSlider } from './widgets/VSlider';

export function Mixer() {
  const channels = useAppStore((s) => s.project.channels);
  const buses = useAppStore((s) => s.project.buses);
  const masterVolume = useAppStore((s) => s.project.masterVolume);
  const { updateChannel, updateBus, setMasterVolume, addBus, removeBus, setSend } =
    useAppStore.getState();

  return (
    <aside className="panel panel-right">
      <div className="panel-title">
        <span>Mixer</span>
        <button className="pill-btn" onClick={addBus} title="Add an aux bus">
          + Bus
        </button>
      </div>
      <div className="mixer">
        {channels.map((ch) => (
          <div
            key={ch.id}
            className="mixer-strip"
            style={{ '--ch-color': ch.color } as CSSProperties}
          >
            <Meter channelId={ch.id} />
            <VSlider
              className="mixer-fader"
              value={ch.volume / 1.5}
              onChange={(v) => updateChannel(ch.id, { volume: v * 1.5 })}
            />
            <Knob
              value={ch.pan}
              onChange={(v) => updateChannel(ch.id, { pan: v })}
              title="Pan (double-click to center)"
            />
            <div className="fx-area">
              {ch.effects.map((slot) => (
                <EffectRow key={slot.id} target={{ kind: 'channel', id: ch.id }} slot={slot} />
              ))}
              <FxAdd target={{ kind: 'channel', id: ch.id }} />
            </div>
            {buses.length > 0 && (
              <div className="sends-area">
                {buses.map((b, i) => (
                  <label key={b.id} className="send-row" title={`Send to ${b.name}`}>
                    <span>B{i + 1}</span>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={ch.sends[b.id] ?? 0}
                      onChange={(e) => setSend(ch.id, b.id, parseFloat(e.target.value))}
                    />
                  </label>
                ))}
              </div>
            )}
            <div className="mixer-name">{ch.name}</div>
          </div>
        ))}

        {buses.map((b) => (
          <div key={b.id} className="mixer-strip bus">
            <Meter busId={b.id} />
            <VSlider
              className="mixer-fader"
              value={b.volume / 1.5}
              onChange={(v) => updateBus(b.id, { volume: v * 1.5 })}
            />
            <Knob
              value={b.pan}
              onChange={(v) => updateBus(b.id, { pan: v })}
              title="Pan (double-click to center)"
            />
            <div className="fx-area">
              {b.effects.map((slot) => (
                <EffectRow key={slot.id} target={{ kind: 'bus', id: b.id }} slot={slot} />
              ))}
              <FxAdd target={{ kind: 'bus', id: b.id }} />
            </div>
            <div className="mixer-name">
              {b.name}
              <button className="bus-remove" onClick={() => removeBus(b.id)} title="Remove bus">
                ×
              </button>
            </div>
          </div>
        ))}

        <div className="mixer-strip master">
          <Meter master />
          <VSlider
            className="mixer-fader"
            value={masterVolume / 1.5}
            onChange={(v) => setMasterVolume(v * 1.5)}
          />
          <div className="mixer-pan-placeholder" />
          <div className="mixer-name">Master</div>
        </div>
      </div>
    </aside>
  );
}